import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input, Select, Textarea } from '../ui/Input';
import { supportApi } from '../../services/api';
import type { SupportTicket } from '../../types';

interface NewTicketFormProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (ticket: SupportTicket) => void;
}

const priorityOptions = [
  { value: 'low', label: 'Lágur' },
  { value: 'medium', label: 'Miðlungs' },
  { value: 'high', label: 'Hár' },
  { value: 'critical', label: 'Brýnt' },
];

const categoryOptions = [
  { value: 'general', label: 'Almennt' },
  { value: 'billing', label: 'Reikningar' },
  { value: 'container', label: 'Gámur' },
  { value: 'technical', label: 'Tæknileg' },
  { value: 'complaint', label: 'Kvörtun' },
];

export function NewTicketForm({ isOpen, onClose, onCreate }: NewTicketFormProps) {
  const [formData, setFormData] = useState({
    customer_email: '',
    customer_name: '',
    subject: '',
    description: '',
    priority: 'medium',
    category: 'general',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.customer_email || !formData.subject || !formData.description) {
      setError('Vinsamlegast fylltu út öll nauðsynleg svæði');
      return;
    }

    if (!formData.customer_email.includes('@')) {
      setError('Netfang verður að vera gilt');
      return;
    }

    setSubmitting(true);
    try {
      const response = await supportApi.createTicket(formData);
      onCreate(response.ticket);

      // Reset form
      setFormData({
        customer_email: '',
        customer_name: '',
        subject: '',
        description: '',
        priority: 'medium',
        category: 'general',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Villa við að búa til beiðni');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Ný þjónustubeiðni"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-red-50 p-4 text-red-800">
            <p className="text-sm">{error}</p>
          </div>
        )}

        <Input
          label="Netfang viðskiptavinar *"
          type="email"
          name="customer_email"
          placeholder="nafn@netfang.is"
          value={formData.customer_email}
          onChange={handleChange}
          required
        />

        <Input
          label="Nafn viðskiptavinar"
          type="text"
          name="customer_name"
          placeholder="Jón Jónsson"
          value={formData.customer_name}
          onChange={handleChange}
        />

        <Input
          label="Efni *"
          type="text"
          name="subject"
          placeholder="Stutt lýsing á málinu"
          value={formData.subject}
          onChange={handleChange}
          required
        />

        <Textarea
          label="Lýsing *"
          name="description"
          placeholder="Nákvæm lýsing á vandamálinu eða beiðninni..."
          value={formData.description}
          onChange={handleChange}
          rows={6}
          required
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Forgangur"
            name="priority"
            options={priorityOptions}
            value={formData.priority}
            onChange={handleChange}
          />

          <Select
            label="Flokkur"
            name="category"
            options={categoryOptions}
            value={formData.category}
            onChange={handleChange}
          />
        </div>

        <div className="flex items-center justify-end gap-4 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Hætta við
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Býr til...' : 'Búa til beiðni'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
